import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { filter, lastValueFrom } from "rxjs";
import { CacheService } from "src/app/services/cache.service";
import { FeatureService } from "src/app/services/features.service";
import { NetworkService } from "src/app/services/network.service";
import { BackCoordinatorService } from "src/app/services/back-coordinator.service";
import { Capacitor } from "@capacitor/core";

@Component({
  selector: "app-footer",
  templateUrl: "./footer.component.html",
  styleUrls: ["./footer.component.scss"],
})
export class FooterComponent implements OnInit, OnChanges {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    public featureService: FeatureService,
    private cacheService: CacheService,
    private networkService: NetworkService,
    public backCoordinator: BackCoordinatorService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.featureBaseUrl) {
      this.getFooterIcons();
    }
  }

  @Input() featureBaseUrl: string;

  currentRoute = "";
  footerIcons;
  defaultPath;
  featureId: string;
  isIOS = Capacitor.getPlatform() === "ios";
  homeIconData = {
    defaultPath: "/home",
    footerIcons: [
      {
        isSelected: false,
        iconSrc: "/assets/redesign/home/home-icon.svg",
        negIconSrc: "/assets/redesign/home/home-neg-icon.svg",
        name: "Home",
      },
    ],
  };

  backIconData = {
    iconSrc: "/assets/redesign/back-icon.svg",
    name: "Back",
  };

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateCurrentRouteFromUrl(event.url);
        this.setIsHomeSelected();
      });
    this.updateCurrentRouteFromUrl(this.router.url);
    this.addIcons();
    // Don't call getFooterIcons() here - it will be called from ngOnChanges when featureBaseUrl is set

    // Listen for navigation changes from React Native features (e.g., protocols switching adult/pediatric)
    this.setupReactNavigationListener();
  }

  private setupReactNavigationListener(): void {
    const handleReactNavigationChange = (event: any) => {
      const { navigationParam, source, featureId } = event.detail || {};
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
      console.log("[NAV] 🦶 Footer: Received reactNavigationChange event");
      console.log("[NAV] 📍 navigationParam:", navigationParam);
      console.log("[NAV] 🔧 source:", source);
      console.log("[NAV] 🆔 featureId:", featureId);
      console.log("[NAV] 🌐 Current URL:", this.router.url);

      // Update the current route to reflect the new navigation parameter
      // This will trigger footer icons to update their selection state
      if (navigationParam) {
        console.log(
          "[NAV] 🔄 Footer: Updating current route to:",
          navigationParam
        );
        this.currentRoute = navigationParam;
        this.setIsHomeSelected();

        // Trigger change detection to update the UI
        this.cdr.detectChanges();
        console.log(
          "[NAV] ✅ Footer: Updated current route and triggered change detection"
        );
      }
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    };

    window.addEventListener(
      "reactNavigationChange",
      handleReactNavigationChange
    );
    console.log("[NAV] ✅ Footer: Listening for reactNavigationChange events");
  }

  updateCurrentRoute(temp) {
    this.currentRoute = temp;
  }

  updateCurrentRouteFromUrl(url: string): void {
    const cleanUrl = url.split(/[?#]/)[0];

    // Home is selected on the root shells
    if (cleanUrl === "/home" || cleanUrl === "/" || cleanUrl === "/login") {
      this.currentRoute = "/home";
      return;
    }

    // For React Native feature routes, the logical route is the navigationParam
    // query param (e.g. /adult, /pediatric) so feature icons highlight correctly.
    if (cleanUrl.startsWith("/react-native/")) {
      const queryString = url.split("?")[1] || "";
      const params = new URLSearchParams(queryString);
      const navigationParam = params.get("navigationParam");
      if (navigationParam) {
        this.currentRoute = navigationParam;
        return;
      }
    }

    this.currentRoute = cleanUrl;
  }

  getCurrentRoute() {
    return this.currentRoute;
  }

  getHomeIconData() {
    return this.homeIconData;
  }

  getHomeIsSelected() {
    return this.homeIconData.footerIcons[0].isSelected;
  }

  updateHomeIconData(isSelected): void {
    this.homeIconData.footerIcons[0].isSelected = isSelected;
  }

  isCurrentRouteHome() {
    return this.currentRoute === "/home";
  }

  doesCurrentRouteMatch(url) {
    // Compare against this.currentRoute which is updated by React navigation events
    // instead of the Angular route query param which doesn't change on internal React navigation
    return this.currentRoute === url;
  }

  setIsHomeSelected() {
    if (this.currentRoute === "/home") {
      this.updateHomeIconData(true);
    } else {
      this.updateHomeIconData(false);
    }
  }

  addIcons() {
    this.setIsHomeSelected();

    const footer = document.getElementById("footer");

    function addFooterIcon(fi) {
      const iconDiv = document.createElement("div");
      iconDiv.classList.add("footer-icon");
      footer.appendChild(iconDiv);
    }
  }

  getFooterIcons() {
    console.log("Footer: " + this.featureBaseUrl);
    this.featureService
      .getFeatureDescriptionByBaseUrl(this.featureBaseUrl)
      .then((rslt) => {
        console.log(rslt);
        if (rslt) {
          this.defaultPath = rslt.defaultPath;
          this.footerIcons = rslt.footerIcons;
          this.featureId = rslt.feature_id;
        }
      });
  }

  public iconUrlMap: { [key: string]: string } = {};

  async getIconSrc(fi: any): Promise<void> {
    const iconUrl = `${this.featureBaseUrl}${fi.iconSrc}`;
    let computedUrl: string = "";

    const img = await lastValueFrom(
      this.cacheService.getResource(iconUrl, true)
    );
    console.log("Footer icon", img);

    // now destructure into `data` and `content`
    const data = img.data;

    if (data) {
      console.log(`Footer icon data: ${JSON.stringify(data)}`);
      if (typeof data === "string" && data.startsWith("data:image")) {
        computedUrl = data;
        // } else if (img instanceof Blob) {
        //   computedUrl = URL.createObjectURL(img);
      } else {
        computedUrl = data as string;
      }
      console.log(`Setting iconUrlMap: ${JSON.stringify(computedUrl)}`);
      this.iconUrlMap[iconUrl] = computedUrl;
    } else {
      computedUrl = "";
    }

    // Trigger change detection to update UI when icon is loaded
    this.cdr.markForCheck();
  }

  getIcon(fi: any): string {
    const iconUrl = `${this.featureBaseUrl}${fi.iconSrc}`;
    if (!this.iconUrlMap[iconUrl]) {
      // Trigger asynchronous load if not in cache
      this.getIconSrc(fi);
      return "";
    }
    const data = this.iconUrlMap[iconUrl];
    return "data:image/svg+xml;utf8," + encodeURIComponent(data);
  }

  public negIconUrlMap: { [key: string]: string } = {};

  async getNegIconSrc(fi: any): Promise<void> {
    const iconUrl = `${this.featureBaseUrl}${fi.negIconSrc}`;
    let computedUrl: string = "";

    const img = await lastValueFrom(
      this.cacheService.getResource(iconUrl, true)
    );
    console.log("Footer neg icon", img);

    // now destructure into `data` and `content`
    const data = img.data;

    if (data) {
      console.log(`Footer neg icon data: ${JSON.stringify(data)}`);
      if (typeof data === "string" && data.startsWith("data:image")) {
        computedUrl = data;
        // } else if (img instanceof Blob) {
        //   computedUrl = URL.createObjectURL(img);
      } else {
        computedUrl = data as string;
      }

      console.log(`Setting negIconUrlMap: ${JSON.stringify(computedUrl)}`);
      this.negIconUrlMap[iconUrl] = computedUrl;
    } else {
      computedUrl = "";
    }
    // Trigger change detection
    this.cdr.markForCheck();
  }

  getNegIcon(fi: any): string {
    const iconUrl = `${this.featureBaseUrl}${fi.negIconSrc}`;
    if (!this.negIconUrlMap[iconUrl]) {
      this.getNegIconSrc(fi);
      return "";
    }
    console.log(
      `GET Footer neg icon data: ${JSON.stringify(this.negIconUrlMap[iconUrl])}`
    );
    const data = this.negIconUrlMap[iconUrl];
    return "data:image/svg+xml;utf8," + encodeURIComponent(data);
  }

  onImageError() {
    console.error("Image failed to load!!!!!!!!!!!");
    this.networkService
      .checkInternetConnection()
      .then((isConnected) => {
        if (isConnected) {
          console.log("Internet is active");
        } else {
          this.networkService.markInternetDown();
        }
      })
      .catch((error) =>
        console.error("Unexpected error in connectivity check:", error)
      );
  }

  back(): void {
    this.backCoordinator.goBack();
  }

  navigateToFeature(path: string, queryParam): void {
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("[NAV] 🦶 Footer: navigateToFeature called");
    console.log("[NAV] 📂 path:", path);
    console.log("[NAV] 🔗 queryParam:", queryParam);
    console.log("[NAV] 🌐 Current URL:", this.router.url);

    if (queryParam) {
      // Set feature-base-path in sessionStorage so react-native-bundle can load the feature
      console.log(
        "[NAV] 💾 Footer: Setting feature-base-path in sessionStorage:",
        this.featureBaseUrl
      );
      sessionStorage.setItem("feature-base-path", this.featureBaseUrl);

      // Check if we're already on the React Native route for this feature
      const currentUrl = this.router.url;
      const targetPath = `/react-native/${path.replace(/\//g, "")}`;
      console.log("[NAV] 🎯 Target path:", targetPath);
      console.log(
        "[NAV] ✅ Already on route?",
        currentUrl.startsWith(targetPath)
      );

      if (currentUrl.startsWith(targetPath)) {
        // Already on this React Native feature, just dispatch navigation event
        console.log(
          "[NAV] 🚫 Footer: Already on React Native route, dispatching event only (NO NAVIGATION)"
        );

        // Update query parameters to trigger icon highlighting
        console.log(
          "[NAV] 🔄 Footer: Updating query params for icon highlighting"
        );

        // Update currentRoute immediately for footer icon highlighting
        this.currentRoute = queryParam;
        console.log(
          "[NAV] 🔄 Footer: Updated currentRoute to:",
          this.currentRoute
        );

        this.router
          .navigate([], {
            relativeTo: this.route,
            queryParams: {
              navigationParam: queryParam,
              uuid: this.featureId,
              source: "footer",
            },
            queryParamsHandling: "merge",
            replaceUrl: false,
          })
          .then(() => {
            console.log("[NAV] ✅ Footer: Query params updated");

            // Dispatch the navigation event
            const navigationEvent = new CustomEvent("angularNavigationChange", {
              detail: {
                navigationParam: queryParam,
                featureId: path.replace(/\//g, ""),
                source: "footer",
              },
            });
            console.log(
              "[NAV] 📤 Footer: About to dispatch event with detail:",
              {
                navigationParam: queryParam,
                featureId: path.replace(/\//g, ""),
                source: "footer",
              }
            );
            window.dispatchEvent(navigationEvent);
            console.log("[NAV] ✅ Footer: Event dispatched successfully");

            // Trigger change detection to update UI
            this.cdr.detectChanges();
          });
      } else {
        // Navigate to the React Native route while preserving Angular history
        console.log("[NAV] 🚀 Footer: Navigating to new React Native route");
        console.log("[NAV] 📍 Footer: Navigating to:", [
          "/react-native",
          path.replace(/\//g, ""),
        ]);
        console.log("[NAV] 🔑 Footer: With queryParams:", {
          navigationParam: queryParam,
          uuid: this.featureId,
          source: "footer",
        });
        this.router.navigate(["/react-native", path.replace(/\//g, "")], {
          queryParams: {
            navigationParam: queryParam,
            uuid: this.featureId,
            source: "initial",
          },
          replaceUrl: false,
        });
      }
    } else {
      console.log("[NAV] 🏠 Footer: No queryParam, navigating to:", path);
      this.router.navigate([path]);
    }
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  }
}
